import { defineStore } from "pinia";
import {
  getAddresses,
  getAddress,
  createAddress,
  updateAddress,
  deleteAddress,
} from "@/api/services/esupplyvet/addressesService";
import { getClinicAddresses } from "@/api/orders";
import AuthService from "@/services/authService";
import { useRegisterStore } from "../register";
import { useDelegueStore } from "../delegueStore";

export const useAddressesStore = defineStore("addresses", {
  state: () => ({
    addresses: [],
    clientClinicAddresses: [],
    currentAddress: null,
    loading: false,
    error: null,
  }),

  getters: {
    sortedAddresses: state => {
      return [...state.addresses].sort((a, b) => a.id - b.id);
    },
    clinicAddresses: state => {
      return state.clientClinicAddresses.length > 0
        ? state.clientClinicAddresses
        : state.addresses.filter(address => address.veterinary);
    },
    homeAddresses: state => {
      return state.addresses.filter(address => !address.veterinary);
    },
    hasAddresses: state => {
      return state.addresses.length > 0 || state.clientClinicAddresses.length > 0;
    },
    addressesCount: state => {
      return state.addresses.length + state.clientClinicAddresses.length;
    },
  },

  actions: {
    async fetchAddresses() {
      this.loading = true;
      this.error = null;

      // Initialize stores
      const registerStore = useRegisterStore();
      const delegueStore = useDelegueStore();

      const role = registerStore.user?.role;
      const isClient = role === "client";
      const isDelegue = role === "delegues";

      // Get vet ID for delegates
      const vetId = isDelegue ? delegueStore.getSelectedVeterinarian?.id : null;

      try {
        if (isClient) {
          const response = await getClinicAddresses();
          this.clientClinicAddresses = response.data || [];
          const responseAdresses = await getAddresses();
          this.addresses = responseAdresses.data || [];
        } else if (isDelegue) {
          // For delegates, pass role and vetId to getAddresses
          const response = await getAddresses(role, vetId);
          this.addresses = response.data || [];
          this.clientClinicAddresses = [];
        } else {
          // For other roles (veterinaire, proprietaire, etc.)
          const response = await getAddresses();
          this.addresses = response.data || [];
          this.clientClinicAddresses = [];
        }
      } catch (error) {
        this.error = error.response?.data?.message || "Erreur lors du chargement des adresses";
        console.error("Erreur fetchAddresses:", error);
      } finally {
        this.loading = false;
      }
    },
    /**
     * Récupère une adresse par son ID
     * @param {number} addressId - ID de l'adresse
     */
    async fetchAddress(addressId) {
      this.loading = true;
      this.error = null;

      try {
        const response = await getAddress(addressId);
        this.currentAddress = response.data;
        return response.data;
      } catch (error) {
        this.error = error.response?.data?.message || "Erreur lors du chargement de l'adresse";
        console.error("Erreur fetchAddress:", error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Met à jour une adresse
     * @param {number} addressId - ID de l'adresse
     * @param {Object} addressData - Données d'adresse à mettre à jour
     */
    async updateAddress(addressId, addressData) {
      this.loading = true;
      this.error = null;

      try {
        const enrichedData = this.enrichAddressWithJWT(addressData);
        const response = await updateAddress(addressId, enrichedData);

        const index = this.addresses.findIndex(addr => addr.id === addressId);
        if (index !== -1) {
          this.addresses[index] = response.data;
        }
        if (this.currentAddress?.id === addressId) {
          this.currentAddress = response.data;
        }

        return response.data;
      } catch (error) {
        this.error = error.response?.data?.message || "Erreur lors de la mise à jour de l'adresse";
        console.error("Erreur updateAddress:", error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Crée une nouvelle adresse
     * @param {Object} addressData - Données d'adresse à créer
     */
    async createAddress(addressData) {
      this.loading = true;
      this.error = null;

      console.log("createAddress called with:", addressData);

      try {
        const enrichedData = this.enrichAddressWithJWT(addressData);
        const response = await createAddress(enrichedData);
        this.addresses.push(response.data);
        return response.data;
      } catch (error) {
        this.error = error.response?.data?.message || "Erreur lors de la création de l'adresse";
        console.error("Erreur createAddress:", error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Supprime une adresse
     * @param {number} addressId - ID de l'adresse à supprimer
     */
    async deleteAddress(addressId) {
      this.loading = true;
      this.error = null;

      try {
        await deleteAddress(addressId);
        this.addresses = this.addresses.filter(addr => addr.id !== addressId);
        this.clientClinicAddresses = this.clientClinicAddresses.filter(addr => addr.id !== addressId);

        if (this.currentAddress?.id === addressId) {
          this.currentAddress = null;
        }

        return true;
      } catch (error) {
        this.error = error.response?.data?.message || "Erreur lors de la suppression de l'adresse";
        console.error("Erreur deleteAddress:", error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Réinitialise l'état du store
     */
    resetState() {
      this.addresses = [];
      this.clientClinicAddresses = [];
      this.currentAddress = null;
      this.loading = false;
      this.error = null;
    },

    clearError() {
      this.error = null;
    },

    /**
     * Enrichit les données d'adresse avec firstName/lastName du JWT
     * @param {Object} formData - Données du formulaire
     * @returns {Object} Données enrichies pour l'API
     */
    enrichAddressWithJWT(formData) {
      const tokenPayload = AuthService.getTokenPayload();
      return {
        firstName: tokenPayload?.first_name || "",
        lastName: tokenPayload?.last_name || "",
        phoneNumber: formData.phoneNumber,
        cellphone: formData.cellphone,
        email: formData.email || "",
        company: formData.company || null,
        countryCode: formData.country || "",
        provinceCode: null,
        provinceName: null,
        street: formData.address || "",
        city: formData.city || "",
        postcode: formData.postalCode || "",
        message: formData.message || null,
      };
    },
  },

  persist: {
    key: "supplyvet-addresses",
    storage: localStorage,
    paths: ["addresses", "clientClinicAddresses"],
  },
});
